import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Layout from './components/Layout';
import ChatPage from './pages/ChatPage';
import InsightsPage from './pages/InsightsPage';
import AssessmentsPage from './pages/AssessmentsPage';
import ProfilePage from './pages/ProfilePage';
import SettingsPage from './pages/SettingsPage';
import SignInPage from './pages/SignInPage';
import SignUpPage from './pages/SignUpPage';
import OnboardingPage from './pages/OnboardingPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { OnboardingProvider, useOnboarding } from './contexts/OnboardingContext';
import { ChatProvider } from './contexts/ChatContext';
import LayoutSkeleton from './components/skeletons/LayoutSkeleton';

// Protected route component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  // Show the skeleton layout while checking authentication
  if (loading) {
    return <LayoutSkeleton />;
  }
  
  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/signin" />;
  }
  
  return children;
};

// Component to check if onboarding is needed
const OnboardingCheck = ({ children }) => {
  const { loading, hasCompletedOnboarding } = useOnboarding();
  const location = useLocation();
  
  // If we're already on the onboarding page, just render the children
  if (location.pathname === '/onboarding') {
    return children;
  }
  
  // Show the skeleton layout while we're checking onboarding status
  if (loading) {
    return <LayoutSkeleton />;
  }
  
  // We have a definitive answer now
  
  // If user has NOT completed onboarding, redirect to onboarding
  if (hasCompletedOnboarding === false) {
    return <Navigate to="/onboarding" />;
  }
  
  // If user has completed onboarding or we're not sure, show the requested page
  return children;
};

// Component that decides whether to show onboarding or redirect to home
const OnboardingOrRedirect = () => {
  const { loading, hasCompletedOnboarding } = useOnboarding();
  
  // Show the skeleton layout while we check status
  if (loading) {
    return <LayoutSkeleton />;
  }
  
  // We have a definitive answer now
  
  // If user has completed onboarding, redirect to home
  if (hasCompletedOnboarding === true) {
    return <Navigate to="/" />;
  }
  
  // If user has not completed onboarding or status is unknown, show onboarding page
  return <OnboardingPage />;
};

function AppRoutes({ username }) {
  // Initialize sidebar state from localStorage or default to true (collapsed)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved !== null ? JSON.parse(saved) : true;
  });

  // Save sidebar state to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', JSON.stringify(sidebarCollapsed));
  }, [sidebarCollapsed]);

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const { isAuthenticated } = useAuth();

  return (
    <Routes>
      {/* Auth routes */}
      <Route 
        path="/signin" 
        element={isAuthenticated ? <Navigate to="/" /> : <SignInPage />} 
      />
      <Route 
        path="/signup" 
        element={isAuthenticated ? <Navigate to="/" /> : <SignUpPage />} 
      />
      
      {/* Public route for privacy policy */}
      <Route 
        path="/privacy" 
        element={
          <Layout sidebarCollapsed={sidebarCollapsed} toggleSidebar={toggleSidebar}>
            <PrivacyPage />
          </Layout>
        } 
      />
      
      {/* Public route for terms of service */}
      <Route 
        path="/terms" 
        element={
          <Layout sidebarCollapsed={sidebarCollapsed} toggleSidebar={toggleSidebar}>
            <TermsPage />
          </Layout>
        } 
      />
      
      {/* Onboarding route - now with smart redirect */}
      <Route 
        path="/onboarding" 
        element={
          <ProtectedRoute>
            <OnboardingOrRedirect />
          </ProtectedRoute>
        } 
      />
      
      {/* Protected routes wrapped in Layout */}
      <Route 
        path="/" 
        element={
          <ProtectedRoute>
            <OnboardingCheck>
              <Layout sidebarCollapsed={sidebarCollapsed} toggleSidebar={toggleSidebar}>
                <ChatPage username={username} />
              </Layout>
            </OnboardingCheck>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/chat" 
        element={
          <ProtectedRoute>
            <OnboardingCheck>
              <Layout sidebarCollapsed={sidebarCollapsed} toggleSidebar={toggleSidebar}>
                <ChatPage />
              </Layout>
            </OnboardingCheck>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/insights" 
        element={
          <ProtectedRoute>
            <OnboardingCheck>
              <Layout sidebarCollapsed={sidebarCollapsed} toggleSidebar={toggleSidebar}>
                <InsightsPage />
              </Layout>
            </OnboardingCheck>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/learn" 
        element={
          <ProtectedRoute>
            <OnboardingCheck>
              <Layout sidebarCollapsed={sidebarCollapsed} toggleSidebar={toggleSidebar}>
                <AssessmentsPage />
              </Layout>
            </OnboardingCheck>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/profile" 
        element={
          <ProtectedRoute>
            <OnboardingCheck>
              <Layout sidebarCollapsed={sidebarCollapsed} toggleSidebar={toggleSidebar}>
                <ProfilePage />
              </Layout>
            </OnboardingCheck>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/settings" 
        element={
          <ProtectedRoute>
            <OnboardingCheck>
              <Layout sidebarCollapsed={sidebarCollapsed} toggleSidebar={toggleSidebar}>
                <SettingsPage />
              </Layout>
            </OnboardingCheck>
          </ProtectedRoute>
        } 
      />
    </Routes>
  );
}

function AppContent({ username }) {
  return (
    <Router>
      <AppRoutes username={username} />
    </Router>
  );
}

function App({ username = "User" }) {
  return (
    <AuthProvider>
      <OnboardingProvider>
        <ChatProvider>
          <AppContent username={username} />
        </ChatProvider>
      </OnboardingProvider>
    </AuthProvider>
  );
}

export default App; 